class ProductCompatibility extends HTMLElement {
  connectedCallback() {
    if (this.dataset.compatibilityInitialized === 'true') return;

    this.dialog = this.querySelector('[data-compatibility-dialog]');
    this.opener = this.querySelector('[data-compatibility-open]');
    this.closeButton = this.querySelector('[data-compatibility-close]');
    this.filter = this.querySelector('[data-compatibility-filter]');

    if (!this.dialog || !this.opener) return;

    this.dataset.compatibilityInitialized = 'true';
    this.opener.addEventListener('click', () => this.open());
    this.closeButton?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('close', () => this.onClose());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.dialog.close();
    });
    this.filter?.addEventListener('input', () => this.applyFilter());
  }

  open() {
    this.opener.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('product-compatibility-is-open');
    if (!this.dialog.open) this.dialog.showModal();

    window.requestAnimationFrame(() => {
      (this.filter || this.closeButton)?.focus({ preventScroll: true });
    });
  }

  onClose() {
    this.opener.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('product-compatibility-is-open');

    if (this.filter?.value) {
      this.filter.value = '';
      this.applyFilter();
    }

    this.opener.focus({ preventScroll: true });
  }

  normalise(value) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  collectText(element) {
    const parts = [];
    const walk = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) parts.push(child.nodeValue);
        if (child.nodeType === Node.ELEMENT_NODE) walk(child);
      });
    };
    walk(element);
    return parts.join(' ');
  }

  searchText(element) {
    if (!element.compatibilitySearchText) {
      const normalised = this.normalise(this.collectText(element));
      element.compatibilitySearchText = {
        normalised,
        compact: normalised.replace(/ /g, ''),
        words: normalised ? normalised.split(' ') : []
      };
    }
    return element.compatibilitySearchText;
  }

  withinDistance(first, second, maximum) {
    if (Math.abs(first.length - second.length) > maximum) return false;
    if (first === second) return true;

    let twoRowsAgo = null;
    let previous = Array.from({ length: second.length + 1 }, (unused, index) => index);

    for (let firstIndex = 1; firstIndex <= first.length; firstIndex += 1) {
      const current = [firstIndex];
      let rowBest = firstIndex;

      for (let secondIndex = 1; secondIndex <= second.length; secondIndex += 1) {
        const cost = first[firstIndex - 1] === second[secondIndex - 1] ? 0 : 1;
        let value = Math.min(
          previous[secondIndex] + 1,
          current[secondIndex - 1] + 1,
          previous[secondIndex - 1] + cost
        );

        if (
          firstIndex > 1 &&
          secondIndex > 1 &&
          first[firstIndex - 1] === second[secondIndex - 2] &&
          first[firstIndex - 2] === second[secondIndex - 1]
        ) {
          value = Math.min(value, twoRowsAgo[secondIndex - 2] + 1);
        }

        current[secondIndex] = value;
        rowBest = Math.min(rowBest, value);
      }

      if (rowBest > maximum) return false;
      twoRowsAgo = previous;
      previous = current;
    }

    return previous[second.length] <= maximum;
  }

  tokenMatches(token, text) {
    if (text.normalised.includes(token)) return true;
    if (token.length > 1 && text.compact.includes(token.replace(/ /g, ''))) return true;
    if (token.length < 4) return false;

    const allowedDistance = token.length >= 8 ? 2 : 1;
    return text.words.some((word) => this.withinDistance(token, word, allowedDistance));
  }

  tokensMatch(tokens, element) {
    const text = this.searchText(element);
    return tokens.every((token) => this.tokenMatches(token, text));
  }

  rankItem(item, tokens, query) {
    const text = this.searchText(item);
    if (!tokens.every((token) => this.tokenMatches(token, text))) return null;
    if (text.normalised === query) return 0;
    if (text.normalised.startsWith(query)) return 1;
    if (text.normalised.includes(query)) return 2;
    if (text.compact.includes(query.replace(/ /g, ''))) return 3;
    return 4;
  }

  applyFilter() {
    const query = this.normalise(this.filter?.value || '');
    const tokens = query ? query.split(' ') : [];
    let visibleRows = 0;

    this.querySelectorAll('[data-compatibility-group]').forEach((group) => {
      let groupVisible = false;

      group.querySelectorAll('[data-compatibility-row]').forEach((row) => {
        const show = tokens.length === 0 || this.tokensMatch(tokens, row);
        row.hidden = !show;
        if (show) {
          groupVisible = true;
          visibleRows += 1;
        }
      });

      group.hidden = !groupVisible;
    });

    this.querySelectorAll('[data-compatibility-item]').forEach((item) => {
      item.classList.remove('is-best-match');
    });

    let bestMatch = null;
    if (tokens.length) {
      this.querySelectorAll('[data-compatibility-row]:not([hidden])').forEach((row) => {
        const brand = row.querySelector('.product-compatibility-group__brand');
        const itemTokens = brand
          ? tokens.filter((token) => !this.tokenMatches(token, this.searchText(brand)))
          : tokens;

        if (!itemTokens.length) return;

        row.querySelectorAll('[data-compatibility-item]').forEach((item) => {
          const score = this.rankItem(item, itemTokens, query);
          if (score === null) return;

          const length = this.searchText(item).normalised.length;
          if (!bestMatch || score < bestMatch.score || (score === bestMatch.score && length < bestMatch.length)) {
            bestMatch = { item, score, length };
          }
        });
      });
    }

    bestMatch?.item.classList.add('is-best-match');

    const empty = this.querySelector('[data-compatibility-empty]');
    if (empty) empty.hidden = visibleRows > 0;

    const status = this.querySelector('[data-compatibility-status]');
    if (status) {
      const bestMatchLabel = bestMatch
        ? this.collectText(bestMatch.item).replace(/\s+/g, ' ').trim()
        : '';

      status.textContent = tokens.length === 0
        ? ''
        : visibleRows === 0
          ? 'No matching compatibility groups'
          : `${bestMatchLabel ? `Best match: ${bestMatchLabel}. ` : ''}${visibleRows} matching ${
              visibleRows === 1 ? 'brand group' : 'brand groups'
            }`;
    }
  }
}

if (!customElements.get('product-compatibility')) {
  customElements.define('product-compatibility', ProductCompatibility);
}
